import { useMemo } from 'react';
import {
  type ContactAddress,
  type FunnelChannelSession,
  type FunnelAddress,
  ConversationChannel,
  SenderAvailabilityResponse,
} from 'src/api/generated';
import {
  getWhatsAppAddresses,
  createAddressDropdownOptions,
  getWhatsAppFunnelAddresses,
} from '../../../../utils/whatsapp/address.util';
import { useAutoSelection } from './auto-selection';
import { useSessionMatching } from './session-matching';
import { useSenderAvailabilities } from 'src/modules/shared/hooks/api/messaging/sender-availability';
import { normalizeWhatsAppPhoneNumber } from 'src/modules/shared/utils/formatting/whatsapp.util';
/**
 * Parameters for the useAddressSelection hook
 */
interface UseAddressSelectionParams {
  /** Array of contact addresses to filter for WhatsApp compatibility */
  contactAddresses: ContactAddress[];
  /** Array of funnel addresses to filter for WhatsApp compatibility */
  funnelAddresses: FunnelAddress[];
  /** Array of channel sessions for session matching */
  channelSessions: FunnelChannelSession[];
  /** Whether the composer uses synthetic transport without a provider connection. */
  isTestConversation?: boolean;
  /** Initial addresses or channel-session history are still loading. */
  isLoading?: boolean;
}

/**
 * Return type for the useAddressSelection hook
 */
interface UseAddressSelectionReturn {
  /** Whether real sender choices still need their initial availability evidence. */
  availabilityLoading: boolean;
  /** First failed availability lookup, distinct from confirmed unavailability. */
  availabilityError: Error | null;
  /** Filtered WhatsApp-compatible contact addresses (to addresses) */
  whatsappContactAddresses: ContactAddress[];
  /** Currently selected to address ID */
  selectedToAddressId: number | null;
  /** Function to set the selected to address ID */
  setSelectedToAddressId: (id: number | null) => void;
  /** Currently selected to address object */
  selectedToAddress: ContactAddress | null;
  /** Dropdown options for to addresses */
  toAddressDropdownOptions: { id: number; content: string }[];

  /** Filtered WhatsApp-compatible funnel addresses (from addresses) */
  whatsappFunnelAddresses: FunnelAddress[];
  /** Currently selected from address ID */
  selectedFromAddressId: number | null;
  /** Function to set the selected from address ID */
  setSelectedFromAddressId: (id: number | null) => void;
  /** Currently selected from address object */
  selectedFromAddress: FunnelAddress | null;
  /** Dropdown options for from addresses */
  fromAddressDropdownOptions: { id: number; content: string }[];

  /** Corresponding channel session based on selected addresses */
  correspondingSession: FunnelChannelSession | null;
  /** Whether the corresponding session is active */
  isSessionActive: boolean;
}

/**
 * Main hook for managing WhatsApp address selection and session matching
 */
export const useAddressSelection = ({
  contactAddresses,
  funnelAddresses,
  channelSessions,
  isTestConversation = false,
  isLoading = false,
}: UseAddressSelectionParams): UseAddressSelectionReturn => {
  const whatsappContactAddresses = useMemo(
    () => getWhatsAppAddresses(contactAddresses),
    [contactAddresses]
  );

  const whatsappFunnelCandidates = useMemo(
    () => getWhatsAppFunnelAddresses(funnelAddresses),
    [funnelAddresses]
  );

  const senderQueries = useMemo(
    () =>
      Array.from(
        new Set(
          whatsappFunnelCandidates
            .map((address) => normalizeWhatsAppPhoneNumber(address.address))
            .filter(Boolean)
        )
      ).map((digits) => ({
        channel: ConversationChannel.WHATSAPP,
        sender: `+${digits}`,
      })),
    [whatsappFunnelCandidates]
  );
  const availabilityQueries = useSenderAvailabilities(
    senderQueries,
    !isTestConversation
  );
  // Query result objects change on refetch; keep selection inputs stable while
  // the confirmed set of normalized phone numbers stays the same.
  const availableSenderKey = senderQueries
    .filter(
      (_, index) =>
        !availabilityQueries[index]?.isError &&
        availabilityQueries[index]?.data?.state ===
          SenderAvailabilityResponse.state.AVAILABLE
    )
    .map((query) => normalizeWhatsAppPhoneNumber(query.sender))
    .join(',');
  const whatsappFunnelAddresses = useMemo(() => {
    if (isTestConversation) return whatsappFunnelCandidates;

    const availableSenders = new Set(
      availableSenderKey.split(',').filter(Boolean)
    );
    return whatsappFunnelCandidates.filter((address) =>
      availableSenders.has(normalizeWhatsAppPhoneNumber(address.address))
    );
  }, [isTestConversation, whatsappFunnelCandidates, availableSenderKey]);
  const availabilityLoading =
    !isTestConversation && availabilityQueries.some((query) => query.isPending);
  const availabilityError = isTestConversation
    ? null
    : availabilityQueries.find((query) => query.error)?.error ?? null;

  const {
    selectedToAddressId,
    selectedFromAddressId,
    setSelectedToAddressId,
    setSelectedFromAddressId,
  } = useAutoSelection({
    whatsappContactAddresses,
    whatsappFunnelAddresses,
    channelSessions,
    isLoading: isLoading || availabilityLoading,
  });

  const selectedToAddress = useMemo(() => {
    if (!selectedToAddressId) return null;

    const address = whatsappContactAddresses.find(
      (addr) => addr.id === selectedToAddressId
    );
    return address ?? null;
  }, [whatsappContactAddresses, selectedToAddressId]);

  const selectedFromAddress = useMemo(() => {
    if (!selectedFromAddressId) return null;

    const address = whatsappFunnelAddresses.find(
      (addr) => addr.id === selectedFromAddressId
    );
    return address ?? null;
  }, [whatsappFunnelAddresses, selectedFromAddressId]);

  const { correspondingSession, isSessionActive } = useSessionMatching({
    selectedContactAddress: selectedToAddress,
    selectedFunnelAddress: selectedFromAddress,
    channelSessions,
  });

  const toAddressDropdownOptions = useMemo(
    () => createAddressDropdownOptions(whatsappContactAddresses),
    [whatsappContactAddresses]
  );

  const fromAddressDropdownOptions = useMemo(
    () => createAddressDropdownOptions(whatsappFunnelAddresses),
    [whatsappFunnelAddresses]
  );

  return {
    availabilityLoading,
    availabilityError,
    whatsappContactAddresses,
    selectedToAddressId,
    setSelectedToAddressId,
    selectedToAddress,
    toAddressDropdownOptions,
    whatsappFunnelAddresses,
    selectedFromAddressId,
    setSelectedFromAddressId,
    selectedFromAddress,
    fromAddressDropdownOptions,
    correspondingSession,
    isSessionActive,
  };
};
